import React, { createRef, useRef, useState } from 'react';
import { Card, Link, TextStyle, Tippy } from '@superops/ui-design-system';
import FroalaEditor from 'react-froala-wysiwyg';
import { SVGIcon } from '@superops/super-icons';
import envConfig from '@nile/home/src/Config/env';
import {
  EDITOR_CONFIG,
  EVENT,
  MAX_CHARACTER,
  SOURCE
} from '@nile/components/src/Pages/SystemTrayChatPage/constants';
import 'froala-editor/js/plugins/char_counter.min.js';
import { isEmpty } from 'lodash';
import {
  fetchApi,
  PromptModal,
  getHeaderValue,
  Loader,
  toast,
  getTranslatedText,
  useActivityTracker
} from '@nile/components';
import { useIntl } from 'react-intl';
import { Slide, toast as reactToast } from 'react-toastify';
import { takeSnapShot } from './utils/screenshot';
import RequesterScreenshotConfirmation from './subpages/RequesterScreenshotConfirmation';
import TypingText from './subpages/TypingText';

function ChatEditor(props) {
  const {
    customHeaders,
    sendMessage,
    source,
    threadData,
    generateConversationId,
    chatStatus,
    setChatStatus,
    isTyping
  } = props;

  const intl = useIntl();
  const agentProp = JSON.parse(sessionStorage.getItem('agentProperties'));

  const editorRef = createRef();

  const [template, setTemplate] = useState('');

  const [attachmentList, setAttachmentList] = useState([]);
  const attachmentListRef = useRef([]);
  attachmentListRef.current = attachmentList;
  const [isUploading, setIsUploading] = useState(false);
  const [convId, setConvId] = useState(null);

  const [convLimitExceed, setConvLimitExceed] = useState(false);
  const convLimitExceedRef = useRef(false);
  convLimitExceedRef.current = convLimitExceed;
  const [characterCount, setChatCount] = useState(0);

  const isWeb = source === SOURCE.WEB;

  const handleModalChange = (modal) => {
    setTemplate(modal);
    sendMessage({
      data: null,
      event: EVENT.STOPPED_TYPING_CONVERSATION,
      conversationId: 'TYPING'
    });
  };
  const getNextId = () => {
    return generateConversationId()
      .then((res) => {
        const nextId = res?.data?.getNextIdentifier || null;
        sessionStorage.setItem('screenshotId', nextId);
        if (nextId) {
          takeSnapShot({
            customHeaders: customHeaders,
            formData: {
              module: 'CONVERSATION_THREAD_ATTACHMENT',
              details: {
                threadId: threadData?.threadId,
                conversationId: nextId
              }
            }
          });
        }
      })
      .catch((err) => null);
  };

  const takeRequesterSnapshot = () => {
    setChatStatus((prevState) => ({
      ...prevState,
      screenCapture: true
    }));

    try {
      useActivityTracker({
        event: 'CHAT SCREENSHOT - REQUESTER'
      });
    } catch (err) {
      console.log(err);
    }

    reactToast(
      <RequesterScreenshotConfirmation
        onAccept={getNextId}
        threadData={threadData}
        customHeaders={customHeaders}
        closeToastContainer={() => {
          reactToast.dismiss();
          setChatStatus((prevState) => ({
            ...prevState,
            screenCapture: false
          }));
        }}
        intl={intl}
      />,
      {
        position: 'bottom-center',
        autoClose: false,
        hideProgressBar: true,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        closeButton: false,
        theme: 'light',
        transition: Slide
      }
    );
  };

  function uploadFile(formData) {
    const requestOptions = {
      method: 'POST',
      credentials: 'include',
      body: formData,
      headers: isWeb
        ? getHeaderValue()
        : {
            'asset-id': customHeaders?.AssetId || agentProp?.AssetId,
            'asset-token': customHeaders?.AssetToken || agentProp?.AssetToken,
            'app-acc': customHeaders?.AppAcc || agentProp?.AppAcc,
            reqType: 'assetUser'
          }
    };

    try {
      fetchApi(`${envConfig.fileRoot}/upload`, requestOptions)
        .then((response) => {
          return response.json();
        })
        .then((list) => {
          if (list?.data) {
            setAttachmentList((prevState) => [...prevState, ...list?.data]);
          }
        })
        .catch((error) => {
          toast('error', error);
        })
        .finally(() => setIsUploading(false));
    } catch (err) {
      console.log(err);
    }
  }

  function handleAttachment(files) {
    if (isEmpty(files)) {
      return;
    }
    setIsUploading(true);
    const formData = new FormData();
    files.forEach((file, idx) => {
      formData.append(`files`, file);
    });

    formData.append('module', 'CONVERSATION_THREAD_ATTACHMENT');

    if (convId) {
      if (threadData?.threadId)
        formData.append(
          'details',
          JSON.stringify({
            threadId: threadData?.threadId,
            conversationId: convId
          })
        );
      uploadFile(formData);
      return;
    }

    generateConversationId().then((res) => {
      setConvId(res?.data?.getNextIdentifier);
      if (threadData?.threadId)
        formData.append(
          'details',
          JSON.stringify({
            threadId: threadData?.threadId,
            conversationId: res?.data?.getNextIdentifier
          })
        );
      uploadFile(formData);
    });
  }

  const removeFile = (fle) => {
    const fileAfterRemove = attachmentList.filter(
      (el) => el?.fileName !== fle?.fileName
    );

    setAttachmentList(fileAfterRemove);
  };

  const clearTemplate = () => {
    setTemplate('');
    setAttachmentList([]);
    setConvId(null);
  };

  const sendMessageToUser = (content = null) => {
    const msg = content || template;
    if (msg || !isEmpty(attachmentListRef.current)) {
      sendMessage({
        message: true,
        content: msg,
        attachments: isEmpty(attachmentListRef.current)
          ? null
          : attachmentListRef.current,
        conversationId: convId
      });
      clearTemplate();
    }
  };

  return (
    <div className="chat-editor position-absolute full-width p1020 b0 nonDraggable">
      <Card rounded>
        <div className="p1520">
          {isTyping && (
            <div className="position-relative">
              <TypingText userActiveConversation={threadData} isWeb={isWeb} />
            </div>
          )}
          <div className="full-width">
            <FroalaEditor
              ref={editorRef}
              tag="textarea"
              config={{
                ...EDITOR_CONFIG,
                placeholderText: getTranslatedText(
                  intl,
                  'msp.chat.editor.placeholder'
                ),
                events: {
                  'charCounter.exceeded': () => {
                    setConvLimitExceed(true);
                  },
                  contentChanged: function() {
                    const editor = this;
                    const charCount = editor.charCounter.count();
                    if (charCount < MAX_CHARACTER) {
                      setConvLimitExceed(false);
                    } else {
                      setConvLimitExceed(true);
                    }
                    setChatCount(charCount);
                  },
                  blur: function() {
                    sendMessage({
                      data: null,
                      event: EVENT.STOPPED_TYPING_CONVERSATION,
                      conversationId: 'TYPING'
                    });
                  },
                  keydown: function(e) {
                    if (convLimitExceedRef.current) return;
                    sendMessage({
                      data: null,
                      event: EVENT.TYPING_CONVERSATION,
                      conversationId: 'TYPING'
                    });
                    if (e.shiftKey && e.key === 'Enter') {
                      e.preventDefault();
                      e.stopPropagation();
                      return;
                    }
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      e.stopPropagation();
                      const editor = this;
                      const content = editor.html.get();
                      const replacedString = content?.replace(
                        /<p>\s*<br>\s*<\/p>/g,
                        ''
                      );
                      if (!isEmpty(replacedString)) {
                        sendMessageToUser(replacedString);
                        setTimeout(
                          () =>
                            sendMessage({
                              data: null,
                              event: EVENT.STOPPED_TYPING_CONVERSATION,
                              conversationId: 'TYPING'
                            }),
                          600
                        );
                      }
                      editor.html.set('');
                      return false;
                    }
                  }
                }
              }}
              model={template}
              onModelChange={handleModalChange}
            />
          </div>
          <div className="fx actr mt5">
            {convLimitExceed && (
              <div className="flex g3 mbt5">
                <TextStyle variation="negative">{`${characterCount}/${MAX_CHARACTER}`}</TextStyle>
              </div>
            )}
            {isUploading && (
              <div className="flex-center mbt5">
                <Loader />
              </div>
            )}
            {!isEmpty(attachmentList) && (
              <div className="flex g3 scroll">
                {attachmentList?.map((el) => (
                  <div className="w50">
                    <Card border="secondary">
                      <div className="flex-center g10 p5 ptr">
                        <div className="flex f11 ellipsis">
                          <Link>{el?.fileName}</Link>
                        </div>
                        <div className="flex" onClick={() => removeFile(el)}>
                          <SVGIcon name="Delete" size="sm" />
                        </div>
                      </div>
                    </Card>
                  </div>
                ))}
              </div>
            )}
            <div className="flex jend f11 full-width g10">
              {chatStatus?.sessionConnected && (
                <>
                  {!isWeb && (
                    <>
                      {chatStatus?.screenCapture ? (
                        <div className="flex">
                          <span className="agentLoadingSmall" />
                        </div>
                      ) : (
                        <div
                          aria-hidden
                          className="flex-center ptr"
                          id="screenshot"
                          onClick={takeRequesterSnapshot}
                        >
                          <SVGIcon name="ScreenShot" />
                        </div>
                      )}
                    </>
                  )}

                  <div className="flex-center ptr">
                    <PromptModal
                      id="addAttachmentsBtn"
                      limitFileSize={20}
                      description="Upload/drop files here"
                      handleSubmit={handleAttachment}
                      bulkFileValidator
                      title="Add attachments"
                      noIcon
                      svg={
                        /* Editor icons hence not changed to SVG */
                        <SVGIcon name="FileAttachment" color="#323232" />
                      }
                      link
                      allowMultiple
                      acceptType={props?.acceptType}
                    />
                  </div>
                </>
              )}
              <Tippy
                title={
                  convLimitExceed
                    ? getTranslatedText(
                        intl,
                        'msp.chat.editor.character.limit.exceed'
                      )
                    : null
                }
              >
                <div
                  className={`flex-center ptr ${
                    convLimitExceed ? 'disabled cursor-not-allowed' : ''
                  }`}
                  onClick={() => {
                    if (convLimitExceed) return;
                    sendMessageToUser();
                  }}
                  aria-hidden
                >
                  <SVGIcon name="SendWhite" />
                </div>
              </Tippy>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}

export default ChatEditor;
